// Performance optimization service

// Third-party script loader with defer
export const loadScriptDeferred = (src: string, id: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.body.appendChild(script);
  });
};

// Preload critical assets with proper priority
export const preloadCriticalAssets = (assets: string[]): void => {
  assets.forEach((asset, index) => {
    const fileExtension = asset.split('.').pop()?.toLowerCase();
    let asType = 'image';
    
    if (fileExtension === 'css') asType = 'style';
    else if (fileExtension === 'js') asType = 'script';
    else if (fileExtension === 'woff2' || fileExtension === 'woff' || fileExtension === 'ttf') asType = 'font';
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = asset;
    link.as = asType as any;
    
    // Set high priority for the first few assets only
    if (index < 3) {
      link.setAttribute('fetchpriority', 'high');
    }
    
    if (asType === 'font') link.setAttribute('crossorigin', 'anonymous');
    
    document.head.appendChild(link);
  });
};

// Enhanced intersection observer for lazy loading with better thresholds
export const initLazyLoading = (): void => {
  if (!('IntersectionObserver' in window)) return;
  
  const lazyLoadObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const lazyElement = entry.target as HTMLElement;
        
        if (lazyElement.tagName === 'IMG' && lazyElement.dataset.src) {
          const img = lazyElement as HTMLImageElement;
          img.src = img.dataset.src;
          delete img.dataset.src;
          
          // Add load event listener to fade in the image
          img.addEventListener('load', () => {
            img.classList.add('loaded');
          });
        }
        
        lazyElement.classList.remove('lazy-load');
        lazyLoadObserver.unobserve(lazyElement);
      }
    });
  }, { 
    rootMargin: '300px', // Increased rootMargin for earlier loading
    threshold: 0.01 // Lower threshold to start loading sooner
  });
  
  document.querySelectorAll('.lazy-load').forEach(element => {
    lazyLoadObserver.observe(element);
  });
};

// Add cache headers to fetch requests
export const fetchWithCache = async (url: string, options?: RequestInit): Promise<Response> => {
  const defaultOptions: RequestInit = {
    headers: {
      'Cache-Control': 'max-age=3600',
    },
    ...options
  };
  
  return fetch(url, defaultOptions);
};

// Optimize paint by minimizing layout shifts
export const minimizeLayoutShifts = (): void => {
  document.documentElement.style.setProperty('content-visibility', 'auto');
  document.documentElement.style.setProperty('contain-intrinsic-size', '1px 5000px');
};

// Preconnect to important domains
export const preconnectToDomains = (domains: string[]): void => {
  domains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    document.head.appendChild(link);
    
    // Also add DNS-prefetch as fallback
    const dnsLink = document.createElement('link');
    dnsLink.rel = 'dns-prefetch';
    dnsLink.href = domain;
    document.head.appendChild(dnsLink);
  });
};

// Defer non-critical CSS
export const deferNonCriticalCSS = (href: string): void => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.media = 'print';
  link.onload = function() {
    link.media = 'all';
  };
  document.head.appendChild(link);
};

// Optimize font loading with font-display swap
export const optimizeFontLoading = (): void => {
  const style = document.createElement('style');
  style.textContent = `
    @font-face {
      font-family: 'Inter';
      font-style: normal;
      font-weight: 400;
      font-display: swap;
      src: local('Inter Regular'), local('Inter-Regular'), url(https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hjA.woff2) format('woff2');
    }
    @font-face {
      font-family: 'Caveat';
      font-style: normal;
      font-weight: 400;
      font-display: swap;
      src: local('Caveat Regular'), local('Caveat-Regular'), url(https://fonts.gstatic.com/s/caveat/v10/Wnz6HAc5bAfYB2Q7YjYYmg8.woff2) format('woff2');
    }
  `;
  document.head.appendChild(style);
};

// Add Resource Hints
export const addResourceHints = (): void => {
  // List of domains that will be used
  const domains = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ];
  
  preconnectToDomains(domains);
};

// Register early page transitions
export const registerEarlyPageTransitions = (): void => {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    
    if (anchor && !anchor.target && anchor.host === window.location.host) {
      const href = anchor.getAttribute('href');
      if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
        // Preload the page resource
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        document.head.appendChild(link);
      }
    }
  }, { passive: true });
};

// Optimize images specifically
export const optimizeImages = (): void => {
  // Find all images that don't have explicit width and height
  document.querySelectorAll('img:not([width]):not([height])').forEach(img => {
    // Set natural dimensions if available
    if ((img as HTMLImageElement).naturalWidth) {
      img.setAttribute('width', (img as HTMLImageElement).naturalWidth.toString());
      img.setAttribute('height', (img as HTMLImageElement).naturalHeight.toString());
    }
    
    // Add loading="lazy" for images below the fold
    if (!img.hasAttribute('loading') && !isElementInViewport(img)) {
      img.setAttribute('loading', 'lazy');
    }
  });
};

// Helper function to check if element is in viewport
const isElementInViewport = (el: Element): boolean => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

// Initialize all performance optimizations
export const initPerformanceOptimizations = (): void => {
  // Add resource hints
  addResourceHints();
  
  // Preconnect to important domains
  preconnectToDomains(['https://fonts.googleapis.com', 'https://fonts.gstatic.com']);
  
  // Optimize font loading
  optimizeFontLoading();
  
  // Initialize lazy loading
  initLazyLoading();
  
  // Minimize layout shifts
  minimizeLayoutShifts();
  
  // Register early page transitions
  registerEarlyPageTransitions();
  
  // Optimize images
  optimizeImages();
  
  // Critical assets to preload
  preloadCriticalAssets([
    "/lovable-uploads/00525eb1-861d-450e-aded-1102c0cae4e7.png", // Main logo
    "/lovable-uploads/4b3bea26-06d2-4efd-a04b-f6046d794008.png", // Avatar
  ]);
};
